<template>
	<span class="employee-link-wrap">
		<button
			v-if="hasIdentity"
			:id="targetId"
			type="button"
            :aria-label="usesRequestPanel || iconOnly || avatarOnly ? `Контакты и сведения о сотруднике: ${displayName}` : null"
            :title="avatarOnly ? displayName : usesRequestPanel ? 'Контакты и сведения о сотруднике' : null"
			class="employee-link"
            :class="{'employee-link--panel': usesRequestPanel, 'employee-link--avatar-only': avatarOnly}"
			@mouseenter="onPreviewFocus"
			@focus="onPreviewFocus"
			@click.stop.prevent="onClick"
		>
			<span v-if="!compact && avatarUrl" class="employee-link__avatar">
				<img :key="avatarUrl" :src="avatarUrl" :alt="displayName" @error="handleAvatarError">
			</span>
			<span v-else-if="!compact" class="employee-link__avatar employee-link__avatar--fallback">
				{{ initials }}
			</span>
            <i v-if="iconOnly && !avatarOnly" class="far fa-address-card" aria-hidden="true"></i>
            <span v-else-if="!avatarOnly" class="employee-link__name"><slot>{{ displayName }}</slot></span>
            <i v-if="usesRequestPanel && !iconOnly && !avatarOnly" class="far fa-address-card employee-link__contact-icon" aria-hidden="true"></i>
			<b-badge v-if="isInactive" variant="secondary" class="employee-link__inactive">Неактивен</b-badge>
		</button>
		<span v-else>{{ displayName }}</span>
		<a
			v-if="showTelegramUsername && inlineTelegramUsername && inlineTelegramUrl"
			:href="inlineTelegramUrl"
			target="_blank"
			rel="noopener"
			class="employee-link__handle"
			@click.stop
		>
			@{{ inlineTelegramUsername }}
		</a>

		<b-popover
            ref="popover"
			v-if="hasIdentity && !usesRequestPanel"
			:target="targetId"
			triggers="hover focus click"
			placement="auto"
			custom-class="employee-card-popover"
			@hide="keepPhoneMenuOpen"
		>
			<div class="employee-card">
				<div class="employee-card__head">
					<div v-if="cardAvatarUrl" class="employee-card__avatar">
						<img :src="cardAvatarUrl" :alt="cardDisplayName">
					</div>
					<div v-else class="employee-card__avatar employee-card__avatar--fallback">
						{{ cardInitials }}
					</div>
					<div class="employee-card__title">
						<div class="employee-card__name">{{ cardDisplayName }}</div>
						<b-badge v-if="isInactive" variant="secondary" class="mt-1">Неактивен</b-badge>
						<div v-if="positionText" class="employee-card__muted">{{ positionText }}</div>
						<div v-if="departmentText" class="employee-card__muted">{{ departmentText }}</div>
					</div>
				</div>

				<div v-if="loading" class="employee-card__loading">
					<b-spinner small class="mr-1"></b-spinner>
					Загрузка
				</div>
				<div v-else-if="loadError" class="employee-card__body" role="status">
                    {{ loadError }}
                    <b-button size="sm" variant="link" @click="loadCard">Повторить</b-button>
                </div>
                <div v-else class="employee-card__body">
					<div v-if="card && card.phone" class="employee-card__row">
						<i class="fas fa-phone"></i>
						<EmployeePhoneMenu :value="card.phone" :telegram-username="phoneTelegramUsername"
						                   @menu-open="phoneMenuOpen = $event" />
					</div>
					<a v-if="card && card.gmail" :href="`mailto:${card.gmail}`" class="employee-card__row">
						<i class="fas fa-envelope"></i>
						<span>{{ card.gmail }}</span>
					</a>
					<a v-if="card && card.telegram_url" :href="card.telegram_url" target="_blank" class="employee-card__row">
						<i class="fab fa-telegram-plane"></i>
						<span>{{ card.telegram_username ? `@${card.telegram_username}` : 'Telegram' }}</span>
					</a>
					<div v-if="!hasContactRows" class="employee-card__empty">
						Контакты скрыты настройками
					</div>
				</div>

				<router-link
					v-if="canOpenFullCard"
					:to="{name: 'employee-details', params: {id: card.employee_id}}"
					class="employee-card__open"
				>
					Открыть карточку
				</router-link>
			</div>
		</b-popover>
	</span>
</template>

<script>
import api from '@/utils/api';
import EmployeePhoneMenu from '@/components/EmployeePhoneMenu.vue';
import {telegramUsername} from '@/utils/employeeTelegram';
import {employeeContactCard} from '@/utils/employeeContactCard';

let uid = 0;

export default {
	name: 'EmployeeLink',
    inject: {requestEmployeeContext: {default: null}},
	components: {EmployeePhoneMenu},
	props: {
        openInPanel: {type: Boolean, default: false},
        employeeId: {type: [Number, String], default: null},
        compact: {type: Boolean, default: false},
        iconOnly: {type: Boolean, default: false},
        avatarOnly: {type: Boolean, default: false},
		user: {
			type: Object,
			default: null
		},
		userId: {
			type: [Number, String],
			default: null
		},
		fallback: {
			type: String,
			default: 'Не указан'
		},
		nameOrder: {
			type: String,
			default: 'first-last'
		},
		showTelegramUsername: {
			type: Boolean,
			default: false
		}
	},
	data() {
		uid += 1;
		return {
			phoneMenuOpen: false,
            loadError: '',
			targetId: `employee-link-${uid}`,
			card: null,
			loading: false,
			loaded: false,
			avatarLoadFailed: false,
			cardRequestVersion: 0
		};
	},
	computed: {
        resolvedEmployeeId() { return this.employeeId || (this.user && this.user.employee_id) || null; },
        hasIdentity() { return Boolean(this.resolvedEmployeeId || this.resolvedUserId); },
        identityKey() { return this.resolvedEmployeeId ? 'employee:' + this.resolvedEmployeeId : 'user:' + this.resolvedUserId; },
        usesRequestPanel() { return Boolean(this.requestEmployeeContext && (this.openInPanel || this.requestEmployeeContext.isEnabled())); },
        identity() { return {employeeId: this.resolvedEmployeeId, userId: this.resolvedUserId, name: this.displayName}; },
		phoneTelegramUsername() {
			return telegramUsername(this.card && this.card.telegram_username)
				|| telegramUsername(this.card && this.card.telegram_url) || '';
		},
		resolvedUserId() {
			return this.userId || (this.user && (this.user.user_id || this.user.id));
		},
		baseUser() {
			return this.card || this.user || {};
		},
		isInactive() {
			return this.baseUser && this.baseUser.active === false;
		},
		displayName() {
			return this.nameFromUser(this.user || this.card) || this.fallback;
		},
		cardDisplayName() {
			return this.nameFromUser(this.card || this.user) || this.fallback;
		},
		initials() {
			return this.initialsFromUser(this.user || this.card);
		},
		cardInitials() {
			return this.initialsFromUser(this.card || this.user);
		},
		knownAvatarUrl() {
			return this.avatarUrlFromUser(this.user) || this.avatarUrlFromUser(this.card);
		},
		knownAvatarUnavailable() {
			return [this.user, this.card].some(user => {
				return user && Object.prototype.hasOwnProperty.call(user, 'has_avatar') && user.has_avatar === false;
			});
		},
		avatarUrl() {
			if (this.avatarLoadFailed) {
				return null;
			}
			if (this.knownAvatarUrl) {
				return this.knownAvatarUrl;
			}
			if (this.resolvedEmployeeId || !this.resolvedUserId || this.knownAvatarUnavailable) {
				return null;
			}
			return `/api/users/${encodeURIComponent(this.resolvedUserId)}/avatar`;
		},
		cardAvatarUrl() {
			return this.card && this.card.avatar_url ? this.card.avatar_url : this.avatarUrl;
		},
		departmentText() {
			const departments = this.card && Array.isArray(this.card.departments) ? this.card.departments : [];
			return departments.map(item => item && item.name).filter(Boolean).join(', ');
		},
		positionText() {
			return (this.card && this.card.position) || null;
		},
		hasContactRows() {
			return Boolean(this.card && (this.card.phone || this.card.gmail || this.card.telegram_url));
		},
		canOpenFullCard() {
			return Boolean(this.card && this.card.can_open_card);
		},
		inlineTelegramUsername() {
			return this.normalizeTelegramUsername(
				this.card && this.card.telegram_username
			);
		},
		inlineTelegramUrl() {
			if (this.inlineTelegramUsername) {
				return `https://telegram.me/${this.inlineTelegramUsername}`;
			}
			const user = this.card || {};
			return user.telegram_url || null;
		}
	},
	watch: {
        '$store.state.token'() { this.resetCard(); },
		identityKey() {
            this.resetCard();
		},
		knownAvatarUrl(value, previous) {
			if (value && value !== previous) {
				this.avatarLoadFailed = false;
			}
		}
	},
	methods: {
        onPreviewFocus() {
            if (!this.usesRequestPanel) return this.loadCard();
        },
        resetCard() {
            this.cardRequestVersion++;
            this.card = null;
            this.loadError = '';
            this.loading = false;
            this.loaded = false;
            this.avatarLoadFailed = false;
        },
        onClick(event) {
            if (!this.usesRequestPanel) return this.loadCard();
            this.phoneMenuOpen = false;
            if (this.$refs.popover) this.$refs.popover.$emit('close');
            this.$emit('open');
            this.requestEmployeeContext.open(this.identity, event.currentTarget);
        },
		keepPhoneMenuOpen(event) { if (this.phoneMenuOpen) event.preventDefault(); },
		handleAvatarError() {
			this.avatarLoadFailed = true;
		},
		async loadCard() {
			if (!this.hasIdentity || this.loaded || this.loading) {
				return;
			}
			const identityKey = this.identityKey;
			const requestVersion = this.cardRequestVersion + 1;
			this.cardRequestVersion = requestVersion;
			this.loading = true;
            this.loadError = '';
			try {
				const details = this.usesRequestPanel
                    ? await this.requestEmployeeContext.load(this.identity)
                    : this.resolvedEmployeeId
                        ? (await api.get('/api/employees/' + encodeURIComponent(this.resolvedEmployeeId), {ignoreModal: true})).data
                        : await this.$store.dispatch('getEmployeeContactCard', this.resolvedUserId);
                const card = employeeContactCard(details);
				if (requestVersion === this.cardRequestVersion && identityKey === this.identityKey) {
					this.card = card;
					this.loaded = true;
				}
			} catch (e) {
				if (requestVersion === this.cardRequestVersion) {
					this.loadError = 'Сведения сотрудника недоступны.';
				}
			} finally {
				if (requestVersion === this.cardRequestVersion) {
					this.loading = false;
				}
			}
		},
		nameFromUser(user) {
			if (!user) {
				return null;
			}
			if (user.full_name) {
				return user.full_name;
			}
			const firstName = user.first_name || '';
			const lastName = user.last_name || '';
			const parts = this.nameOrder === 'last-first'
				? [lastName, firstName]
				: [firstName, lastName];
			return parts.join(' ').trim();
		},
		initialsFromUser(user) {
			if (!user) {
				return '?';
			}
			const firstName = user.first_name ? user.first_name.charAt(0) : '';
			const lastName = user.last_name ? user.last_name.charAt(0) : '';
			return `${lastName}${firstName}`.trim() || '?';
		},
		normalizeTelegramUsername(value) {
			const username = String(value || '').trim().replace(/^@+/, '');
			return username || null;
		},
		avatarUrlFromUser(user) {
			if (!user) {
				return null;
			}
			if (user.avatar_url) {
				return user.avatar_url;
			}
			const userId = user.user_id || user.id;
			if (!userId || !user.has_avatar) {
				return null;
			}
			const stampRaw = user.avatar_updated_at ? new Date(user.avatar_updated_at).getTime() : null;
			const stamp = Number.isFinite(stampRaw) ? stampRaw : null;
			return `/api/users/${userId}/avatar${stamp ? `?v=${stamp}` : ''}`;
		}
	}
};
</script>

<style scoped>
.employee-link-wrap {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
	max-width: 100%;
	vertical-align: middle;
}

.employee-link {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	gap: 5px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #0056b3;
	font: inherit;
	text-align: left;
	cursor: pointer;
}

.employee-link:hover .employee-link__name,
.employee-link:focus .employee-link__name {
	text-decoration: underline;
}

.employee-link--panel .employee-link__name {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.employee-link__contact-icon {
    flex-shrink: 0;
    font-size: 0.85em;
}

.employee-link__avatar,
.employee-card__avatar {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	overflow: hidden;
	background: #eef2f5;
	color: #495057;
	font-weight: 700;
}

.employee-link__avatar {
	width: 22px;
	height: 22px;
	border-radius: 50%;
	font-size: 10px;
}

.employee-link__avatar img,
.employee-card__avatar img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.employee-link__name {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.employee-link__inactive {
	font-size: 10px;
	font-weight: 600;
}

.employee-link__handle {
	color: #0b7fab;
	font-size: 12px;
	line-height: 1.2;
	white-space: nowrap;
}

.employee-link__handle:hover,
.employee-link__handle:focus {
	color: #075e80;
	text-decoration: underline;
}

.employee-card {
	width: 280px;
	max-width: 82vw;
}

.employee-card__head {
	display: grid;
	grid-template-columns: 52px minmax(0, 1fr);
	gap: 10px;
	align-items: center;
	margin-bottom: 10px;
}

.employee-card__avatar {
	width: 52px;
	height: 52px;
	border-radius: 8px;
	font-size: 18px;
}

.employee-card__title {
	min-width: 0;
}

.employee-card__name {
	color: #212529;
	font-weight: 700;
	line-height: 1.2;
}

.employee-card__muted {
	margin-top: 2px;
	color: #6c757d;
	font-size: 12px;
	line-height: 1.25;
}

.employee-card__body {
	display: grid;
	gap: 5px;
}

.employee-card__row {
	display: grid;
	grid-template-columns: 18px minmax(0, 1fr);
	gap: 6px;
	align-items: center;
	color: #212529;
	font-size: 13px;
}

.employee-card__row span {
	min-width: 0;
	overflow-wrap: anywhere;
}

.employee-card__empty,
.employee-card__loading {
	color: #6c757d;
	font-size: 13px;
}

.employee-card__open {
	display: inline-flex;
	margin-top: 10px;
	font-size: 13px;
	font-weight: 700;
}
</style>
